import type { Responsive } from '#types/common'
import type { ButtonVariant } from '#types/components/vf/button'
import type { Target } from '#types/target'
import ds from '#root/design-system'

type StyleResult = {
  style: Responsive<ButtonVariant>
  classOverride: Partial<Responsive>
}

/**
 * Reduces a responsive `style` map against `activeButtonMapping` and `activeClassOverride`,
 * returning the remapped button variants and any per-breakpoint class overrides as separate objects.
 *
 * For each breakpoint entry in `style`:
 * - Looks up the replacement variant in `activeButtonMapping`; falls back to the original value.
 * - Looks up the class for **that specific breakpoint** from `activeClassOverride[sourceVariant]`.
 */
const resolveActiveStyle = (
  style: Responsive<ButtonVariant>,
  activeButtonMapping: Record<string, string>,
  activeClassOverride?: Partial<Record<string, Responsive>>,
): StyleResult =>
  Object.keys(ds.breakpoints).reduce<StyleResult>(
    (acc, key) => {
      const value = getValueForBreakpoint(key as keyof Responsive, style)
      if (!value) return acc

      const newStyle = activeButtonMapping[value] || value
      acc.style[key] = newStyle

      const cls = getValueForBreakpoint(key as keyof Responsive, activeClassOverride?.[newStyle])
      if (cls)
        acc.classOverride[key] = cls

      return acc
    },
    { style: {} as Responsive<ButtonVariant>, classOverride: {} as Responsive },
  )

/**
 * Mutates a CMS `Target`'s button style in-place to render the "active" state for navigation/CTA
 * targets that are currently selected. Forces text-link underline to `'always'`, swaps each
 * responsive button variant via `activeButtonMapping`, and optionally writes per-breakpoint class
 * overrides to `target.classOverride` via `activeClassOverride`.
 * @param target - The target to mutate.
 * @param activeButtonMapping - Map of source variant → active variant.
 * @param activeClassOverride - Optional map of source variant → `Responsive<string>` of CSS classes
 *   to apply per breakpoint when that variant is active. Each slot value should be a complete,
 *   static class string (e.g. `'border-b'`, `'md:border-2'`) so UnoCSS can scan it.
 * @category Utils
 */
export const adjustActiveButtonStyle = (
  target: Target,
  activeButtonMapping: Record<string, string> = {},
  activeClassOverride?: Partial<Record<string, Responsive<string>>>,
) => {
  target.textLinkUnderline = 'always'

  if (target.style) {
    const { style, classOverride } = resolveActiveStyle(target.style, activeButtonMapping, activeClassOverride)
    target.style = style
    const overrides = Object.values(classOverride).filter(Boolean)
    if (overrides.length)
      target.classOverride = overrides.join(' ')
  }
}
